/**
 * Phone-call reminders — which officer blocks the user still has to tell about.
 *
 * A block is one officer's contiguous run on one counter (a merged cell on the
 * counter grid). Whenever the roster adds or edits one, the officer has to be
 * phoned. Which blocks count:
 * - SOS and OT officers: every block — their assignments are expected to change;
 * - main officers: only blocks that differ from the initially generated roster
 *   (the ones drawn in red), since the generated plan is what they already know.
 *
 * A call is recorded against the block's signature (counter + timing). Editing
 * the block gives it a signature nobody has phoned about, so the reminder
 * re-arms by itself, without the roster having to track "changed since".
 */

import type { CallAck, OfficerMetadata } from '@/types/api';
import { sc } from '@/utils/uiScale';

export interface CallBlock {
  officerId: string;
  /** 0-based counter_matrix row. */
  counter: number;
  startSlot: number;
  /** Inclusive. */
  endSlot: number;
  signature: string;
}

export const blockSignature = (counter: number, startSlot: number, endSlot: number) =>
  `${counter}:${startSlot}-${endSlot}`;

/** Every block that needs a call, in row then time order. */
export function computeCallBlocks(
  counterMatrix: string[][][],
  officerMetadata: Record<string, OfficerMetadata>,
  initialSchedules: Record<string, number[]> | null | undefined,
): CallBlock[] {
  const blocks: CallBlock[] = [];
  counterMatrix.forEach((slots, counter) => {
    const runs = new Map<string, { start: number; end: number; changed: boolean }>();
    const flush = (officerId: string) => {
      const run = runs.get(officerId);
      if (!run) return;
      runs.delete(officerId);
      const isMain = officerMetadata[officerId]?.officer_type?.toUpperCase() === 'MAIN';
      if (isMain && !run.changed) return;
      blocks.push({
        officerId, counter, startSlot: run.start, endSlot: run.end,
        signature: blockSignature(counter, run.start, run.end),
      });
    };
    for (let slot = 0; slot <= slots.length; slot++) {
      const here = new Set(slots[slot] ?? []);
      for (const officerId of [...runs.keys()]) {
        if (!here.has(officerId) || runs.get(officerId)!.end !== slot - 1) flush(officerId);
      }
      for (const officerId of here) {
        const changed = !!initialSchedules && (initialSchedules[officerId]?.[slot] ?? 0) !== counter + 1;
        const run = runs.get(officerId);
        if (run) { run.end = slot; run.changed ||= changed; } else runs.set(officerId, { start: slot, end: slot, changed });
      }
    }
  });
  return blocks.sort((a, b) => a.counter - b.counter || a.startSlot - b.startSlot);
}

export const ackKey = (officerId: string, signature: string) => `${officerId}|${signature}`;

export function callAckKeys(acks: CallAck[]): Set<string> {
  return new Set(acks.map((a) => ackKey(a.officer_id, a.signature)));
}

export interface OfficerCallState {
  /** Blocks still owed a call. */
  pending: CallBlock[];
  /** All blocks that need a call (pending + done). */
  total: number;
}

export function officerCallStates(blocks: CallBlock[], acked: Set<string>): Map<string, OfficerCallState> {
  const out = new Map<string, OfficerCallState>();
  for (const b of blocks) {
    const st = out.get(b.officerId) ?? { pending: [], total: 0 };
    st.total++;
    if (!acked.has(ackKey(b.officerId, b.signature))) st.pending.push(b);
    out.set(b.officerId, st);
  }
  return out;
}

/** Smallest/largest phone icon (px) pinned to a block's corner — scaled with the desktop UI scale like the rest of the grid. */
const CALL_ICON_MIN = sc(7);
/** A row holding a call icon is at least this tall, so the icon (half of it) is legible. */
export const CALL_ROW_MIN_HEIGHT = CALL_ICON_MIN * 2;
const CALL_ICON_MAX = sc(14);

/**
 * Diameter of the phone icon on a block of the given pixel height: at most half the
 * block, so it never covers the cell. 0 when the block is too short to hold a legible one.
 */
export function callIconDiameter(blockHeight: number): number {
  const d = Math.min(CALL_ICON_MAX, Math.floor(blockHeight / 2));
  return d >= CALL_ICON_MIN ? d : 0;
}
